<script lang="ts">
	import type { SbImage, SbLink } from '../types';
	import EmailCaptureContent from '$lib/emailCaptureContent.svelte';
	import Image from '../components/image.svelte';
	import EmailModal from '../parts/emailModal.svelte';
	import SbRichtext from '../components/sbRichtext.svelte';
	import type { ChewieUMCouponIdentifier } from '$lib/api/chewieUM.types';
	import type { FormState } from '$lib/emailCaptureInputV2.svelte';
	import EmailCaptureInputV2 from '$lib/emailCaptureInputV2.svelte';
	import { fade, fly } from 'svelte/transition';
	import Button from '$lib/buttons/v3/button.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import { onMount } from 'svelte';
	import { storyblok } from '$lib/state/storyblok.svelte';
	import log from '$lib/utils/log';

	interface Props {
		content: ISbStoryData<{
			intro_cta_label: string;
			intro_dismiss_label: string;
			image: SbImage;
			image_desktop: SbImage;
			open_delay: string;
			title: ISbRichtext;
			subtext: ISbRichtext;
			legal_text: ISbRichtext;
			success_title: ISbRichtext;
			success_subtext: ISbRichtext;
			success_cta_title?: string;
			success_cta_link: SbLink;
			button_label: string;
			ignored_url_params: string;
			coupon_identifier: ChewieUMCouponIdentifier;
			coupon_info_text: string;
			placeholder_text: string;
			template: string;
			use_template_headline?: boolean;
			use_template_subtext?: boolean;
			use_template_coupon?: boolean;
			use_template_success_state?: boolean;
			use_template_images?: boolean;
			use_template_settings?: boolean;
		}>['content'];
	}

	let { content }: Props = $props();

	let formState = $state<FormState>(null);

	let confirmed = $state(false);

	let modal = $state<{
		show: () => void;
		hide: () => void;
		remove: () => void;
	}>();

	let contentHeight = $state<number>(0);

	async function mergeTemplate(uuid: string) {
		try {
			const { data } = await storyblok.client.getStories({
				starts_with: 'globals/email-capture-templates/',
				by_uuids: uuid
			});

			if (data.stories.length === 0) return null;

			const template = data.stories[0].content;

			mergedContent = {
				...content,
				...(content.use_template_headline && { headline: template.headline }),
				...(content.use_template_subtext && { subtext: template.subtext }),
				...(content.use_template_images && {
					image_mobile: template.image_mobile,
					image_desktop: template.image_desktop
				}),
				...(content.use_template_settings && {
					placeholder_text: template.placeholder_text,
					open_delay: template.open_delay,
					ignored_url_params: template.ignored_url_params
				}),
				...(content.use_template_coupon && {
					coupon_identifier: template.coupon_identifier,
					coupon_text: template.coupon_text
				}),
				...(content.use_template_success_state && {
					success_headline: template.success_headline,
					success_text: template.success_text,
					success_cta_link: template.success_cta_link,
					success_cta_title: template.success_cta_title
				})
			};
		} catch (err) {
			log.scope('emailCaptureModalV2').exception(err);
		}
	}

	let mounted = $state(false);
	let mergedContent = $state(content);

	onMount(async () => {
		if (content.template) {
			await mergeTemplate(content.template);
		}
		mounted = true;
	});

	let {
		intro_cta_label,
		intro_dismiss_label,
		title,
		subtext,
		success_title,
		success_subtext,
		success_cta_title = '',
		success_cta_link,
		button_label,
		coupon_identifier,
		coupon_info_text,
		placeholder_text,
		legal_text,
		open_delay,
		ignored_url_params,
		image: image_mobile,
		image_desktop,
		use_template_images
	} = $derived(mergedContent);
</script>

{#snippet introContent()}
	<div out:fade={{ duration: 200 }} class="col-start-1 row-start-1">
		<h1
			class={[
				'pb-7 pt-8 text-center text-[36px]/[112%] font-semibold tracking-[-.36px] lg:py-6 lg:text-[48px]/[120%] lg:tracking-[-.48px]',
				{
					'animate-pulse rounded-xl bg-stone-3/50 text-transparent':
						content.use_template_headline && !mounted
				}
			]}
		>
			<SbRichtext text={title} />
		</h1>
		<div class="flex flex-col items-center">
			<Button class="btn-large" onclick={() => (confirmed = true)}>{intro_cta_label}</Button>
			<button
				type="button"
				class="p-3 text-[14px]/[160%] text-brown-1"
				onclick={() => modal?.hide()}>{intro_dismiss_label}</button
			>
		</div>
	</div>
{/snippet}

{#snippet formContent()}
	<div
		class="col-start-1 row-start-1 pt-6 text-center lg:pt-7"
		out:fade={{ duration: 200 }}
		in:fly={{ delay: 200, y: '-25%' }}
	>
		<h1
			class="mb-3 text-[36px]/[112%] font-semibold tracking-[-.36px] lg:mb-5 lg:text-[48px]/[120%] lg:tracking-[-.48px]"
		>
			<SbRichtext text={title} />
		</h1>
		<p
			class={[
				'mb-5 text-balance text-[20px]/[140%] tracking-[-.2px] lg:mb-6 lg:text-[24px]/[140%] lg:tracking-[-.24px]',
				{
					'animate-pulse rounded-xl bg-stone-3/50 text-transparent':
						content.use_template_subtext && !mounted
				}
			]}
		>
			<SbRichtext text={subtext} />
		</p>
		<div class="mx-auto max-w-[436px]">
			<EmailCaptureInputV2
				autofocus
				bind:state={formState}
				buttonLabel={button_label}
				couponIdentifier={coupon_identifier}
				couponText={coupon_info_text}
				placeholderText={placeholder_text}
			></EmailCaptureInputV2>

			<div class="modal-legal-text mt-9 text-center text-[12px]/[160%] text-black/40 lg:mt-5">
				<SbRichtext text={legal_text} />
			</div>
		</div>
	</div>
{/snippet}

{#snippet successContent()}
	<div
		class="col-start-1 row-start-1 flex flex-col items-center pt-18 text-center lg:pt-13"
		in:fly={{ delay: 200, y: '-25%' }}
	>
		<h1
			class="mb-3 text-[36px]/[112%] font-semibold tracking-[-.36px] lg:mb-5 lg:text-[48px]/[120%] lg:tracking-[-.48px]"
		>
			<SbRichtext text={success_title} />
		</h1>
		<p
			class="mb-5 text-balance text-[20px]/[140%] tracking-[-.2px] lg:mb-6 lg:text-[24px]/[140%] lg:tracking-[-.24px]"
		>
			<SbRichtext text={success_subtext} />
		</p>
		{#if success_cta_title.length > 0}
			<Button href={getSbUrl(success_cta_link)}>{success_cta_title}</Button>
		{/if}
	</div>
{/snippet}

<div use:editable={content}>
	<EmailModal
		bind:modal
		openDelay={parseInt(open_delay, 10)}
		ignoredParams={ignored_url_params?.split(',')}
		{title}
		text={subtext}
	>
		{#snippet children()}
			<EmailCaptureContent bind:contentHeight>
				{#snippet content()}
					<div class="relative z-10 grid">
						{#if !confirmed}
							{@render introContent()}
						{:else if formState === 'success'}
							{@render successContent()}
						{:else}
							{@render formContent()}
						{/if}
					</div>
				{/snippet}

				{#snippet image()}
					<div
						class={[
							'absolute inset-x-0 bottom-0 z-0 transition-height duration-600 ease-out',
							{
								'animate-pulse rounded-xl bg-stone-3/50 text-transparent':
									use_template_images && !mounted
							}
						]}
						style={`height: calc(100% - ${contentHeight + 80}px)`}
					>
						<Image
							wrapperClass="h-full"
							class="h-full w-full object-cover object-top {image_desktop ? 'lg:hidden' : ''}"
							src={image_mobile.filename}
							alt={image_mobile.alt ?? ''}
							title={image_mobile.title ?? ''}
							width={430}
							sizes={{
								430: { width: 830 },
								1024: { width: 2048 },
								1440: { width: 2880 },
								1920: { width: 3840 },
								2560: { width: 5120 },
								5120: { width: 7680 }
							}}
						/>
						{#if image_desktop}
							<Image
								wrapperClass="h-full"
								class=" h-full w-full object-cover object-top max-lg:hidden"
								src={image_desktop.filename}
								alt={image_desktop.alt ?? ''}
								title={image_desktop.title ?? ''}
								width={430}
								sizes={{
									430: { width: 830 },
									1024: { width: 2048 },
									1440: { width: 2880 },
									1920: { width: 3840 },
									2560: { width: 5120 },
									5120: { width: 7680 }
								}}
							/>
						{/if}
					</div>
				{/snippet}
			</EmailCaptureContent>
		{/snippet}
	</EmailModal>
</div>

<style lang="scss">
	div.modal-legal-text {
		:global(a) {
			text-decoration: underline;
		}
	}
</style>
